import React, { useState, useEffect } from 'react'
import { useTranslation } from '@structura/i18n'

interface CooperativeUser {
  id: number
  cooperativeUserId: string
  cooperativeUserEmail: string
  cooperativeUserAuth0Id?: string
  cooperativeUserName?: string
  cooperativeUserRole: string
  createdAt: string
}

interface AdminCooperativeUsersProps {
  userId: string | null
  getHeaders: () => HeadersInit
}

const AdminCooperativeUsers: React.FC<AdminCooperativeUsersProps> = ({ userId, getHeaders }) => {
  const { t } = useTranslation()
  const [cooperativeUsers, setCooperativeUsers] = useState<CooperativeUser[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [showAddForm, setShowAddForm] = useState(false)
  const [saving, setSaving] = useState(false)

  // Form state
  const [formUserId, setFormUserId] = useState('')
  const [formEmail, setFormEmail] = useState('')
  const [formAuth0Id, setFormAuth0Id] = useState('')
  const [formName, setFormName] = useState('')
  const [formRole, setFormRole] = useState('Designer')

  const loadCooperativeUsers = async () => {
    if (!userId) return

    setLoading(true)
    setError(null)
    try {
      const response = await fetch(`/api/AdminUsers/${userId}/cooperative`, {
        headers: getHeaders()
      })

      if (!response.ok) {
        if (response.status === 404) {
          // Endpoint might not exist yet, fail silently
          setCooperativeUsers([])
          return
        }
        throw new Error(`Failed to load cooperative users (${response.status})`)
      }

      const data = await response.json()
      setCooperativeUsers(Array.isArray(data) ? data : [])
    } catch (err) {
      console.error('Error loading cooperative users:', err)
      setError(err instanceof Error ? err.message : t('admin.coop.errLoad'))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (userId) {
      loadCooperativeUsers()
    }
  }, [userId])

  const resetForm = () => {
    setFormUserId('')
    setFormEmail('')
    setFormAuth0Id('')
    setFormName('')
    setFormRole('Designer')
    setShowAddForm(false)
  }

  const handleAdd = async () => {
    if (!userId || !formUserId || !formEmail) {
      setError(t('admin.coop.errUserIdEmailRequired'))
      return
    }

    setSaving(true)
    setError(null)
    try {
      const response = await fetch(`/api/AdminUsers/${userId}/cooperative`, {
        method: 'POST',
        headers: {
          ...getHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          cooperativeUserId: formUserId,
          cooperativeUserEmail: formEmail,
          cooperativeUserAuth0Id: formAuth0Id || undefined,
          cooperativeUserName: formName || undefined,
          cooperativeUserRole: formRole
        })
      })

      if (!response.ok) {
        const text = await response.text()
        throw new Error(text || `Failed to add cooperative user (${response.status})`)
      }

      await loadCooperativeUsers()
      resetForm()
    } catch (err) {
      setError(err instanceof Error ? err.message : t('admin.coop.errAdd'))
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async (id: number) => {
    if (!userId || !confirm(t('admin.coop.confirmRemove'))) return

    try {
      const response = await fetch(`/api/AdminUsers/${userId}/cooperative/${id}`, {
        method: 'DELETE',
        headers: getHeaders()
      })

      if (!response.ok) {
        throw new Error(`Failed to delete cooperative user (${response.status})`)
      }

      await loadCooperativeUsers()
    } catch (err) {
      setError(err instanceof Error ? err.message : t('admin.coop.errDelete'))
    }
  }

  if (!userId) {
    return (
      <div className="border rounded p-3 bg-light">
        <div className="d-flex align-items-center mb-2">
          <h6 className="mb-0"><i className="bi bi-people me-2"></i>{t('admin.coop.title')}</h6>
        </div>
        <div className="alert alert-info mb-0 py-2">
          <small><i className="bi bi-info-circle me-1"></i>{t('admin.coop.saveFirst')}</small>
        </div>
      </div>
    )
  }

  return (
    <div className="border rounded p-3 bg-light">
      <div className="d-flex justify-content-between align-items-center mb-2">
        <h6 className="mb-0"><i className="bi bi-people me-2"></i>{t('admin.coop.title')}</h6>
        {!showAddForm && (
          <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => setShowAddForm(true)}>
            <i className="bi bi-plus"></i>
          </button>
        )}
      </div>

      {error && (
        <div className="alert alert-danger alert-dismissible py-1 mb-2">
          <small>{error}</small>
          <button type="button" className="btn-close btn-close-sm" onClick={() => setError(null)}></button>
        </div>
      )}

      {showAddForm && (
        <div className="border rounded p-2 mb-2 bg-white">
          <div className="row g-2">
            <div className="col-6">
              <input
                type="text"
                className="form-control form-control-sm"
                placeholder={t('admin.coop.placeholderUserId')}
                value={formUserId}
                onChange={e => setFormUserId(e.target.value)}
              />
            </div>
            <div className="col-6">
              <input
                type="email"
                className="form-control form-control-sm"
                placeholder={t('admin.coop.placeholderEmail')}
                value={formEmail}
                onChange={e => setFormEmail(e.target.value)}
              />
            </div>
            <div className="col-6">
              <input
                type="text"
                className="form-control form-control-sm"
                placeholder={t('admin.coop.placeholderAuth0')}
                value={formAuth0Id}
                onChange={e => setFormAuth0Id(e.target.value)}
              />
            </div>
            <div className="col-6">
              <input
                type="text"
                className="form-control form-control-sm"
                placeholder={t('admin.coop.placeholderName')}
                value={formName}
                onChange={e => setFormName(e.target.value)}
              />
            </div>
            <div className="col-6">
              <select className="form-select form-select-sm" value={formRole} onChange={e => setFormRole(e.target.value)}>
                <option value="Designer">{t('admin.users.roleDesigner')}</option>
                <option value="Approval">{t('admin.users.roleApproval')}</option>
                <option value="Info">{t('admin.users.roleInfo')}</option>
                <option value="All">{t('admin.users.roleAll')}</option>
              </select>
            </div>
            <div className="col-6">
              <button type="button" className="btn btn-sm btn-success w-50" onClick={handleAdd} disabled={saving}>
                {saving ? '...' : t('admin.coop.add')}
              </button>
              <button type="button" className="btn btn-sm btn-light w-50" onClick={resetForm}>{t('beam.loads.cancel')}</button>
            </div>
          </div>
        </div>
      )}

      {loading && <div className="text-center py-2"><span className="spinner-border spinner-border-sm"></span></div>}

      {!loading && cooperativeUsers.length === 0 && !showAddForm && (
        <small className="text-muted">{t('admin.coop.noCoopUsers')}</small>
      )}

      {!loading && cooperativeUsers.length > 0 && (
        <div className="table-responsive">
          <table className="table table-sm table-bordered mb-0">
            <thead>
              <tr>
                <th className="small">{t('admin.users.thEmail')}</th>
                <th className="small">{t('admin.users.role')}</th>
                <th className="small text-end">{t('admin.coop.thAction')}</th>
              </tr>
            </thead>
            <tbody>
              {cooperativeUsers.map(user => (
                <tr key={user.id}>
                  <td className="small">{user.cooperativeUserEmail}</td>
                  <td className="small"><span className="badge bg-secondary-subtle text-secondary">{user.cooperativeUserRole}</span></td>
                  <td className="text-end">
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-danger py-0"
                      onClick={() => handleDelete(user.id)}
                    >
                      <i className="bi bi-trash"></i>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

export default AdminCooperativeUsers

